<script setup>
import {computed, ref} from "vue";
import {useRouter} from "vue-router";
import {UseMoneyStore} from "@/stores/moneyStore";
import {UseCourierStore} from "@/stores/courierStore";
import {UseProductStore} from "@/stores/productStore";
import GlobalHeader from "@/components/GlobalHeader.vue";
import ProductItem from '@/components/ProductItem.vue';
import {useReturnStore} from "@/stores/returnStore";
import {helpers} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import { useScrollToError } from '@/composables/scrollToError';
import PriceFormatter from "@/components/PriceFormatter.vue";
import DescriptionHeader from "@/components/DescriptionHeader.vue";
import ReturnMethodSelected from "@/components/ReturnMethods/ReturnMethodSelected.vue";
import {useOrderDataStore} from "@/stores/orderDataStore";
import {submitRmaData} from "@/service/submitRmaService";
import {VueSpinner} from "vue3-spinners";
import {useI18n} from "vue-i18n";
import {currentLocale} from "@/locales/localization";



const router = useRouter();
const moneyStore = UseMoneyStore();
const courierStore = UseCourierStore();
const productStore = UseProductStore();
const returnStore = useReturnStore();
const orderDataStore = useOrderDataStore();
const loading = ref(false); // while fetching products from API
const { t, locale } = useI18n()


const form = ref({
  returnPolicy: false
});

const rules = {
  form: {
    returnPolicy: helpers.withMessage(t('summaryPage.agreeRefundPolicy'), () => form.value.returnPolicy === true)
  }
};

const v$ = useVuelidate(rules, form);

const refundPolicyLink = computed(() => {
  return currentLocale.value === 'el'
      ? 'https://thefashionproject.gr/policies/refund-policy'
      : 'https://www.thefashionproject.gr/en/policies/refund-policy';
});

const goToHowToReturnPage = () => {
  router.push("/howToReturn");
};

const goToSuccessPage = async () => {

  const isValid = await v$.value.$validate();
  console.log(isValid)
  console.log(form.value.returnPolicy)
  if (!isValid) {
    useScrollToError();
    return;
  }

  loading.value=true;
  try{
    const rmaData = await submitRmaData({
      orderCode: orderDataStore.order,
      token: orderDataStore.token,
      rmaDeliveryAddress: courierStore.rmaDeliveryAddress,
      shippingMethod: courierStore.selectedCourier,
      totalCost: returnStore.totalExtraCost,
      notes: courierStore.formData_backup.comments,
      refundMethod: orderDataStore.refundMethods.find(m => m.key === moneyStore.selectedHowReturnMoney).type,
      iban: moneyStore.fullIban(),
      ibanName:moneyStore.beneficiaryName,
      ibanBank: moneyStore.ibanBank,
      lines:productStore.lines
    }, locale.value);
    console.log(rmaData)
    orderDataStore.setRmaId(rmaData.id);
    console.log("ID:", rmaData.id);

    //
    // console.log("orderCode : ", orderDataStore.order);
    // console.log("token: ", orderDataStore.token);
    // console.log("rmaDeliveryAddress : ", courierStore.rmaDeliveryAddress);
    // console.log("shippingMethod: ", courierStore.selectedCourier);
    // console.log("totalCost: ", returnStore.totalExtraCost);
    // console.log("notes: ", courierStore.formData_backup.comments);
    // console.log("refundMethod: ", orderDataStore.refundMethods.find(m => m.key === moneyStore.selectedHowReturnMoney).type); // type
    // console.log("iban: ", moneyStore.fullIban());
    // console.log("ibanName: ", moneyStore.beneficiaryName)
    // console.log("lines: ", productStore.lines); // (lineId, qty, notes, returnReason)


    // Clear sessionStorage to remove any old session data
    sessionStorage.clear();
    router.push("/success");
  }catch(error){
    console.log("Error: ", error);
    await router.push("/failure");
  }finally {
    loading.value = false;
  }
};



</script>

<template>
  <div class="flex flex-col min-h-screen w-full">
    <div class="flex-grow sm:flex-none">
      <GlobalHeader :on-back="goToHowToReturnPage" :loader="loading" page="Summary"/>
      <div>
        <DescriptionHeader :label="t('summaryPage.step')"/>

        <div class="bg-background-white border overflow-hidden container mt-10 mb-4 mx-auto px-6">
          <div
              v-for="(product, index) in productStore.selectedProducts"
              :key="product.id"
              class="mt-7"
          >
            <ProductItem :product="product" page="Summary"/>

            <br>
            <hr v-if="index < productStore.selectedProducts.length-1" class="border-bw-darkLightGrey ">
          </div>
        </div>


        <!-- Refund -->
        <ReturnMethodSelected/>


        <!-- Products Return -->
        <div class="bg-background-white border overflow-hidden container mx-auto mb-4">
          <div v-if="courierStore.selectedCourier!=='ACS GR' && courierStore.selectedCourier!=='ACS CY Express' && courierStore.selectedCourier!=='Courier Center'" class="flex justify-between items-center px-6 my-5">
            <div class="flex items-start space-x-9 responsive-body-small">
              <span>
                <span class="flex items-center responsive-body-medium">
                  <strong class="flex items-center">
                    {{ courierStore.selectedCourier }}
                  </strong>
                </span>
                <span class="text-bw-lightDarkGrey text-sm">{{ t('howToReturnPage.returnWith') }} {{ courierStore.selectedCourier }}</span>
              </span>
            </div>
          </div>

          <!-- ACS GR -->
          <div v-else-if="courierStore.selectedCourier==='ACS GR'" class="flex justify-between items-center px-6 my-5">
            <div class="flex items-start space-x-9 responsive-body-medium">
              <div>
                <span>
                  <span class="flex items-center">
                    <strong class="flex items-center">
                      {{ courierStore.selectedCourier }}
                    </strong>
                  </span>
                  <span class="text-bw-lightDarkGrey responsive-body-small">{{ t('howToReturnPage.returnWith') }} {{ courierStore.selectedCourier }}</span>
                </span>
                <div v-if="courierStore.selectedCourier==='ACS GR'" class="font-bold mt-4">
                  <div v-if="courierStore.selectedAddress==='old-address'">
                    <span class="block">{{ orderDataStore.deliveryAddress.address }}, {{ orderDataStore.deliveryAddress.city }}</span>
                    <strong class="italic responsive-body-small block">
                      {{ orderDataStore.deliveryAddress.name }} <br>
                      {{ orderDataStore.deliveryAddress.phone }} <br>
                      {{ orderDataStore.deliveryAddress.email }} <br>
                      {{ t('howToReturnPage.zipCode') }}: {{ orderDataStore.deliveryAddress.zip }} <br>
                      ({{ t('howToReturnPage.orderShippingAddress') }})
                    </strong>
                  </div>
                  <div v-else-if="courierStore.selectedAddress==='edited-address'">
                    <span class="block">{{ courierStore.formData_backup.address }}, {{ courierStore.formData_backup.city }}</span>
                    <strong class="responsive-body-small block">
                      {{ courierStore.formData_backup.name }} <br>
                      {{ courierStore.formData_backup.phone }} <br>
                      {{ courierStore.formData_backup.email }} <br>
                      {{ t('howToReturnPage.zipCode') }}: {{ courierStore.formData_backup.zip }} <br>
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>


          <!-- Courier Center -->
          <div v-else-if="courierStore.selectedCourier==='Courier Center'" class="flex justify-between items-center px-6 my-5">
            <div class="flex items-start space-x-9 responsive-body-medium">
              <div>
                <span>
                  <span class="flex items-center">
                    <strong class="flex items-center">
                      {{ courierStore.selectedCourier }}
                    </strong>
                  </span>
                  <span class="text-bw-lightDarkGrey responsive-body-small">{{ t('howToReturnPage.returnWith') }} {{ courierStore.selectedCourier }}</span>
                </span>
                <div v-if="courierStore.selectedCourier==='Courier Center'" class="font-bold mt-4">
                  <div v-if="courierStore.selectedAddressCC==='old-addressCC'">
                    <span class="block">{{ orderDataStore.deliveryAddress.address }}, {{ orderDataStore.deliveryAddress.city }}</span>
                    <strong class="italic responsive-body-small block">
                      {{ orderDataStore.deliveryAddress.name }} <br>
                      {{ orderDataStore.deliveryAddress.phone }} <br>
                      {{ orderDataStore.deliveryAddress.email }} <br>
                      {{ t('howToReturnPage.zipCode') }}: {{ orderDataStore.deliveryAddress.zip }} <br>
                      ({{ t('howToReturnPage.orderShippingAddress') }})
                    </strong>
                  </div>
                  <div v-else-if="courierStore.selectedAddressCC==='edited-addressCC'">
                    <span class="block">{{ courierStore.formData_backup.address }}, {{ courierStore.formData_backup.city }}</span>
                    <strong class="responsive-body-small block">
                      {{ courierStore.formData_backup.name }} <br>
                      {{ courierStore.formData_backup.phone }} <br>
                      {{ courierStore.formData_backup.email }} <br>
                      {{ t('howToReturnPage.zipCode') }}: {{ courierStore.formData_backup.zip }} <br>
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ACS CY Express -->

          <div v-else-if="courierStore.selectedCourier==='ACS CY Express'" class="flex justify-between items-center px-6 my-5">
            <div class="flex items-start space-x-9 responsive-body-medium">
              <div>
                <span>
                  <span class="flex items-center">
                    <strong class="flex items-center">
                      {{ courierStore.selectedCourier }}
                    </strong>
                  </span>
                  <span class="text-bw-lightDarkGrey responsive-body-small">{{ t('howToReturnPage.returnWith') }} {{ courierStore.selectedCourier }}</span>
                </span>
                <div v-if="courierStore.selectedCourier==='ACS CY Express'" class="font-bold mb-6 mt-4">
                  <div v-if="courierStore.selectedAddress==='old-address'">
                    <span class="block">{{ orderDataStore.deliveryAddress.address }}, {{ orderDataStore.deliveryAddress.city }}</span>
                    <strong class="italic responsive-body-small block">
                      {{ orderDataStore.deliveryAddress.name }} <br>
                      {{ orderDataStore.deliveryAddress.phone }} <br>
                      {{ orderDataStore.deliveryAddress.email }} <br>
                      {{ t('howToReturnPage.zipCode') }}: {{ orderDataStore.deliveryAddress.zip }} <br>
                      ({{ t('howToReturnPage.orderShippingAddress') }})
                    </strong>
                  </div>
                  <div v-else-if="courierStore.selectedAddress==='edited-address'">
                    <span class="block">{{ courierStore.formData_backup.address }}, {{ courierStore.formData_backup.city }}</span>
                    <strong class="responsive-body-small block">
                      {{ courierStore.formData_backup.name }} <br>
                      {{ courierStore.formData_backup.phone }} <br>
                      {{ courierStore.formData_backup.email }} <br>
                      {{ t('howToReturnPage.zipCode') }}: {{ courierStore.formData_backup.zip }} <br>
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="grid place-items-center ">
          <hr class="border border-bw-black overflow-hidden container">
        </div>


        <div class="bg-background-white responsive-body-medium border overflow-hidden container mx-auto px-6 py-3 mt-4">
          <ul class="space-y-2 text-bw-black">
            <li class="flex items-center justify-between">
              <span class="flex-1 text-left">{{ t('summaryPage.totalProducts') }}</span>
              <span class="font-bold "><PriceFormatter :price="returnStore.totalProductsPrice.toString()" /></span>
            </li>

            <li class="flex items-center justify-between">
              <span class="flex-1 text-left">{{ t('summaryPage.returnCost') }}</span>
              <span class="font-bold text-nowrap">- <PriceFormatter :price="returnStore.totalExtraCost.toString()" /></span>
            </li>

            <!-- Separator Line -->
            <li class="border-t-2 border-bw-darkLightGrey my-2"></li>

            <li class="flex items-center justify-between">
              <span v-if="moneyStore.selectedHowReturnMoney === 'iban' || moneyStore.selectedHowReturnMoney === 'viva' || moneyStore.selectedHowReturnMoney === 'shopify'"  class="flex-1 text-left font-bold">{{ t('summaryPage.refundAmount') }}</span>
              <span v-else-if="moneyStore.selectedHowReturnMoney === 'credit'"  class="flex-1 text-left font-bold">{{ t('summaryPage.creditValue') }}</span>
              <span class="font-bold "><PriceFormatter :price="returnStore.finalReturn.toString()" /></span>
            </li>
          </ul>
        </div>


        <div class="flex flex-col responsive-body-small overflow-hidden container mx-auto my-4">
          <div class="flex items-center text-bw-black">
            <label for="policy" class="flex items-center cursor-pointer">
              <input
                  type="checkbox"
                  id="policy"
                  class="accent-bw-black mr-2"
                  v-model="form.returnPolicy"
                  @change="v$.form.$touch"
              >
              <span>
                {{ t('summaryPage.agreePolicy') }}
                <a
                    :href="refundPolicyLink"
                    target="_blank"
                    class="underline focus:outline-2 focus:outline-offset-2 focus:outline-bw-black cursor-pointer"
                >
                  {{ t('summaryPage.refundPolicy') }}
                </a>.
              </span>
            </label>
          </div>

          <p v-if="v$.form.$error" class="text-primary-red mt-1" :class="{ 'is-invalid': v$.form.$error }">
            {{ v$.form.$errors[0].$message }}
          </p>
        </div>
      </div>
    </div>

      <div v-if="!loading" class="flex sticky md:relative bottom-0 md:bottom-auto bg-background-default justify-center items-center ">
        <button
            @click="goToSuccessPage"
            class="border rounded bg-bw-black text-bw-white responsive-body-large mb-2 py-3.5 px-1 overflow-hidden container mx-auto hover:bg-bw-darkGrey focus:outline-2 focus:outline-offset-2 focus:outline-bw-black"
            :class="v$.$invalid ? 'bg-bw-darkGrey' : 'bg-bw-black'"
        >
          {{ t('summaryPage.submitRequest') }}
        </button>
      </div>
      <div v-else class="grid place-content-center mt-10 mb-4">
        <VueSpinner size="20"/>
      </div>
  </div>


</template>
